import React from 'react';
import { disciplineName, disciplineSortIndex } from '../constants/disciplines';
import type { AuthorizationBudget } from '../services/api/types.gen';
import { disciplineAtCapacity } from '../utils/authorizationBudget';
import { parseISOToLocalDate } from '../utils/dateUtils';

/**
 * Display order for the bars: SN first, then the therapies, matching the
 * shared `DISCIPLINE_ORDER`. The server returns whatever order it computed
 * in, and SN is the discipline a scheduler is looking for most often, so it
 * leads. Anything not in the canonical list keeps its server order at the
 * end (`disciplineSortIndex`'s fallback).
 */
/**
 * "5/21 – 7/20", or "12/8/25 – 1/6/26" when the range crosses a year. The year
 * is dropped in the common case: this line sits under a heading, beside a
 * calendar already showing the episode's months, and two four-digit years is
 * most of the width of a line whose whole job is to be glanceable.
 */
const compactRange = (start: string, end: string): string => {
  const from = parseISOToLocalDate(start);
  const to = parseISOToLocalDate(end);
  // An unparseable date drops out rather than rendering "Invalid Date"; the
  // authorization number, where there is one, still tells the groups apart.
  if (!from || !to) {
    return '';
  }
  const sameYear = from.getFullYear() === to.getFullYear();
  const format = (value: Date): string =>
    sameYear
      ? `${value.getMonth() + 1}/${value.getDate()}`
      : `${value.getMonth() + 1}/${value.getDate()}/${String(value.getFullYear()).slice(-2)}`;
  return `${format(from)} – ${format(to)}`;
};

/** "#12345 · 1/1 – 3/1", or whichever half of it exists. */
const headerLabel = (auth: AuthorizationBudget): string =>
  [
    auth.authorization_number ? `#${auth.authorization_number}` : '',
    compactRange(auth.start_date, auth.end_date),
  ]
    .filter(Boolean)
    .join(' · ');

interface AuthorizationBudgetStripProps {
  /**
   * Per-authorization used/allowed rows, computed server-side and read from
   * `GET /episodes/{id}/authorizations/errors`. Counting used to
   * happen here from the full authorization + visit lists; the server owns
   * "which visits count against which authorization" so the strip can't drift
   * from the rule that actually blocks scheduling.
   */
  budget: AuthorizationBudget[];
  /**
   * `requires_authorization` from the same response. Distinguishes "no
   * authorization concept applies here" (render nothing, same as the LUPA
   * strip when it has no thresholds) from "one is required but there are no
   * authorized visit counts on file" (a neutral empty state). Read straight
   * off the response rather than matched against an error-code string, so a
   * backend rename can't silently drop the empty state.
   */
  requiresAuthorization: boolean;
  /**
   * How many visits spend an authorized visit but aren't linked to an active
   * authorization. Those visits are not in `used`, so the counts below read
   * lower than what is actually booked. Zero when there are none.
   */
  unlinkedVisitCount: number;
  /**
   * The request for this data failed, so the counts are unknown rather than
   * absent. Says so instead of falling through to the null/empty branches,
   * where a failed fetch would read as "no authorization applies here".
   */
  unavailable?: boolean;
}

/**
 * Per-discipline used/allowed bars for the episode's active authorization(s),
 * shown beside the Tasks tab calendar so a hard-blocked
 * over-budget schedule attempt isn't a surprise — the budget is visible before
 * the user tries.
 *
 * Same bar register as the LUPA gauge next to it: a label line, a bar, and
 * nothing else. The bar is the whole of the reading — a discipline at or over
 * its limit turns red at the same `used >= allowed` boundary the backend
 * blocks on, so an exactly-full discipline reads as "can't add more" before
 * the user tries.
 *
 * The state is also the bar's accessible name, not only a hover affordance:
 * tooltips never open on touch, and this is read on phones.
 */
const AuthorizationBudgetStrip: React.FC<AuthorizationBudgetStripProps> = ({
  budget,
  requiresAuthorization,
  unlinkedVisitCount,
  unavailable = false,
}) => {
  if (unavailable) {
    return (
      <p className='text-xs text-muted-foreground'>
        Authorization counts unavailable right now.
      </p>
    );
  }

  if (budget.length === 0) {
    if (!requiresAuthorization) {
      return null;
    }
    return (
      <p className='text-xs text-muted-foreground'>
        No authorized visit counts on file for this episode.
      </p>
    );
  }

  return (
    <div className='space-y-4'>
      {budget.map(auth => (
        // `space-y-3` between bars is the LUPA periods' rhythm beside it
        <div key={auth.authorization_id} className='space-y-3'>
          {/* Pulled back up against the heading: this is its second line, not
              a row of its own. The word "authorization" under a block already
              headed "AUTH BUDGET" would be the heading again, so
              the line carries only what tells two authorizations apart: the
              number, and the dates it covers. Every group gets one — an
              episode with two unnumbered authorizations otherwise renders as
              one undivided run of bars, where a second SN bar reads as a
              duplicate rather than as another authorization's budget. */}
          {headerLabel(auth) && (
            <div className='-mt-1 text-[0.7rem] uppercase leading-tight tracking-wider text-gray-600'>
              {headerLabel(auth)}
            </div>
          )}
          {[...auth.disciplines]
            .sort(
              (left, right) =>
                disciplineSortIndex(left.discipline) -
                disciplineSortIndex(right.discipline)
            )
            .map(d => {
              const atCapacity = disciplineAtCapacity(d.used, d.allowed);
              const overLimit = d.used > d.allowed;
              const limitSuffix = overLimit
                ? ' (over limit)'
                : atCapacity
                  ? ' (at limit)'
                  : '';
              // "4 of 4" in red already says the discipline is full; the words
              // only repeat it. Over-limit keeps them, because
              // "5 of 4" is worth naming. The accessible name below keeps both
              // — colour is not available to it.
              const visibleSuffix = overLimit ? limitSuffix : '';
              const full = disciplineName(d.discipline);
              const fillPct =
                d.allowed > 0
                  ? Math.min(100, Math.round((d.used / d.allowed) * 100))
                  : 0;
              return (
                <div
                  key={d.discipline}
                  aria-label={`${full}: ${d.used} of ${d.allowed} visits used${limitSuffix}`}
                >
                  <div className='flex items-baseline justify-between gap-2 text-xs'>
                    <span className='font-medium text-gray-700'>
                      {d.discipline}
                    </span>
                    <span
                      className={`whitespace-nowrap ${
                        atCapacity ? 'text-destructive' : 'text-gray-700'
                      }`}
                    >
                      {d.used} of {d.allowed}
                      {visibleSuffix}
                    </span>
                  </div>
                  <div className='mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100'>
                    {/* Shares the LUPA bars' palette where the two strips mean
                        the same thing: full is the same --destructive red a
                        LUPA period gets. Spending an authorization is not
                        itself good or bad, so an under-limit bar stays neutral
                        grey rather than borrowing the "threshold met" green. */}
                    <div
                      className={`h-full rounded-full ${
                        atCapacity ? 'bg-destructive' : 'bg-gray-400'
                      }`}
                      style={{ width: `${fillPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      ))}
      {unlinkedVisitCount > 0 && (
        // `used` counts only visits linked to the authorization, so an
        // episode with unlinked visits reads lower here than what is
        // actually booked. Say so rather than let the number be trusted as
        // total consumption.
        <p className='text-xs text-muted-foreground'>
          {unlinkedVisitCount} visit{unlinkedVisitCount === 1 ? '' : 's'}{' '}
          unlinked
        </p>
      )}
    </div>
  );
};

export default AuthorizationBudgetStrip;
